import React from 'react';
import { motion } from 'framer-motion';
import { User, Building2, MessageSquare, Mail, Phone, FileCheck, Ruler, CalendarRange, DotSquare as SquareFootage } from 'lucide-react';

interface FormData {
  clientType: string;
  propertyType: string;
  diagnostics: string[];
  name: string;
  email: string;
  phone: string;
  address: string;
  constructionYear: string;
  surface: string;
}

interface DevisFormStepsProps {
  formStep: number;
  formData: FormData;
  onFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onDiagnosticToggle: (diagnostic: string) => void;
}

const defaultFormData: FormData = {
  clientType: '',
  propertyType: '',
  diagnostics: [],
  name: '',
  email: '',
  phone: '',
  address: '',
  constructionYear: '',
  surface: ''
};

const DevisFormSteps: React.FC<DevisFormStepsProps> = ({
  formStep,
  formData = defaultFormData,
  onFormChange,
  onDiagnosticToggle
}) => {
  if (formStep === 1) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2 }}
        className="space-y-2"
      >
        <h4 className="text-white font-semibold text-sm mb-2">Type de client</h4>
        <div className="grid grid-cols-2 gap-2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => onFormChange({ target: { name: 'clientType', value: 'particulier' } } as any)}
            className={`p-2 rounded-lg flex items-center justify-center text-sm font-medium ${
              formData.clientType === 'particulier'
                ? 'bg-white text-dk-blue'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <User className="h-4 w-4 mr-1.5" />
            Particulier
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => onFormChange({ target: { name: 'clientType', value: 'professionnel' } } as any)}
            className={`p-2 rounded-lg flex items-center justify-center text-sm font-medium ${
              formData.clientType === 'professionnel'
                ? 'bg-white text-dk-blue'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <Building2 className="h-4 w-4 mr-1.5" />
            Professionnel
          </motion.button>
        </div>

        <div className="space-y-2 mt-3">
          <h4 className="text-white font-semibold text-sm mb-2">Diagnostics souhaités</h4>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              ...(formData.clientType === 'professionnel' 
                ? [
                    ['DPE', <FileCheck key="dpe" className="h-3.5 w-3.5 mr-1" />],
                    ['Amiante (DTA)', <FileCheck key="amiante" className="h-3.5 w-3.5 mr-1" />],
                    ['Termites', <FileCheck key="termites" className="h-3.5 w-3.5 mr-1" />],
                    ['ERP', <FileCheck key="erp" className="h-3.5 w-3.5 mr-1" />],
                    ['Mesurages', <Ruler key="mesurages" className="h-3.5 w-3.5 mr-1" />]
                  ]
                : [
                    ['DPE', <FileCheck key="dpe" className="h-3.5 w-3.5 mr-1" />],
                    ['Audit énergétique', <FileCheck key="audit" className="h-3.5 w-3.5 mr-1" />],
                    ['Amiante', <FileCheck key="amiante" className="h-3.5 w-3.5 mr-1" />],
                    ['Électricité', <FileCheck key="elec" className="h-3.5 w-3.5 mr-1" />],
                    ['Gaz', <FileCheck key="gaz" className="h-3.5 w-3.5 mr-1" />],
                    ['Plomb', <FileCheck key="plomb" className="h-3.5 w-3.5 mr-1" />],
                    ['Termites', <FileCheck key="termites" className="h-3.5 w-3.5 mr-1" />],
                    ['Mesurages', <Ruler key="mesurages" className="h-3.5 w-3.5 mr-1" />],
                    ['ERP', <FileCheck key="erp" className="h-3.5 w-3.5 mr-1" />]
                  ]
              )
            ].map(([diagnostic, icon]) => (
              <motion.button
                key={diagnostic}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={() => onDiagnosticToggle(diagnostic as string)}
                className={`p-1.5 rounded-lg text-xs flex items-center justify-center font-medium ${
                  formData.diagnostics.includes(diagnostic as string)
                    ? 'bg-white text-dk-blue'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {icon}
                {diagnostic}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>
    );
  }

  if (formStep === 2) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2 }}
        className="space-y-2"
      >
        <h4 className="text-white font-semibold text-sm mb-2">Type de bien</h4>
        <select
          name="propertyType"
          value={formData.propertyType}
          onChange={onFormChange}
          className="w-full bg-white/10 border border-white/30 rounded-lg p-2 text-sm text-white font-medium hover:bg-white/20 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
        >
          <option value="" className="bg-dk-blue text-white">Sélectionnez le type de bien</option>
          <option value="maison" className="bg-dk-blue text-white">Maison</option>
          <option value="appartement" className="bg-dk-blue text-white">Appartement</option>
          <option value="local" className="bg-dk-blue text-white">Local commercial</option>
          <option value="terrain" className="bg-dk-blue text-white">Terrain</option>
        </select>

        <div className="space-y-2 mt-2">
          <div>
            <label htmlFor="constructionYear" className="block text-white text-xs font-medium mb-0.5">
              Année de construction *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <CalendarRange className="h-4 w-4 text-white/70" />
              </div>
              <input
                type="number"
                id="constructionYear"
                name="constructionYear"
                value={formData.constructionYear}
                onChange={onFormChange}
                min="1800"
                max={new Date().getFullYear()}
                required
                className="w-full bg-white/10 border border-white/30 rounded-lg py-1.5 pl-8 pr-3 text-sm text-white placeholder-white/70 font-medium hover:bg-white/20 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                placeholder="Ex: 1990"
              />
            </div>
          </div>

          <div>
            <label htmlFor="surface" className="block text-white text-xs font-medium mb-0.5">
              Surface en m² *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <SquareFootage className="h-4 w-4 text-white/70" />
              </div>
              <input
                type="number"
                id="surface"
                name="surface"
                value={formData.surface}
                onChange={onFormChange}
                min="1"
                required
                className="w-full bg-white/10 border border-white/30 rounded-lg py-1.5 pl-8 pr-3 text-sm text-white placeholder-white/70 font-medium hover:bg-white/20 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                placeholder="Ex: 85"
              />
            </div>
          </div>
        </div>

        <div className="relative mt-2">
          <MessageSquare className="absolute left-2.5 top-2 h-4 w-4 text-white" />
          <textarea
            name="address"
            value={formData.address}
            onChange={onFormChange}
            placeholder="Adresse du bien"
            rows={2}
            className="w-full bg-white/10 border border-white/30 rounded-lg py-1.5 pl-8 pr-3 text-sm text-white placeholder-white/70 font-medium hover:bg-white/20 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      className="space-y-2"
    >
      <div className="relative">
        <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white" />
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onFormChange}
          placeholder="Votre nom"
          className="w-full bg-white/10 border border-white/30 rounded-lg py-2 pl-8 pr-3 text-sm text-white placeholder-white/70 font-medium hover:bg-white/20 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
        />
      </div>
      <div className="relative">
        <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white" />
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={onFormChange}
          placeholder="Votre email"
          className="w-full bg-white/10 border border-white/30 rounded-lg py-2 pl-8 pr-3 text-sm text-white placeholder-white/70 font-medium hover:bg-white/20 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
        />
      </div>
      <div className="relative">
        <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white" />
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={onFormChange}
          placeholder="Votre téléphone"
          className="w-full bg-white/10 border border-white/30 rounded-lg py-2 pl-8 pr-3 text-sm text-white placeholder-white/70 font-medium hover:bg-white/20 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
        />
      </div>
    </motion.div>
  );
};

export default DevisFormSteps;